/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

//TODO
// - figure out some better place to put this file

//NEXT STEPS
// - get Ticket setup to accept multiple initialization options
// - make typings work
// - figure out design for "appEvents"

import firebase from 'firebase/compat/app';
import { AngularFireList, AngularFireAction, DatabaseSnapshot } from '@angular/fire/compat/database';
import {
	Observable,
	Subject,
	BehaviorSubject,
	ReplaySubject,
	Subscription,
	ConnectableObservable,
	queueScheduler,
	merge,
	concat,
	from,
	combineLatest,
	of,
	EMPTY,
	defer,
} from 'rxjs';
import {
	catchError,
	takeUntil,
	filter,
	map,
	groupBy,
	debounce,
	switchMap,
	observeOn,
	tap,
	multicast,
	publish,
	mergeMap,
} from 'rxjs/operators';
import { drop, alternativeBuffer } from 'src/app/plain-javascript/services/rxjs-internal-extensions';

import { fbReporting } from 'src/app/plain-javascript/background-scripts/fb-reporting';

import { makeStack } from 'utils/basic-utils';

import { FancyAction } from './FancyAction';
import { FancyActionTypes } from './FancyActionTypes';
import { AngularLandConnection, RefOrObservable } from './interfaces';
import { FancyIdJoin } from './FancyIdJoin';

// import { FancySupplementalAction, SupplementalActionTypes } from 'src/app/app-data/actions/supplemental-actions';

import { IFancyActionTrackerObj } from '../services/fancy-action-tracker-factory.service';

import { renderQueue } from 'src/app/plain-javascript/services/RenderQueue';
import { LocalSnapshot, LocalAction } from './LocalSnapshot';
import { IHasType, isActionIncluded } from '../util/is-action-included';
import { goodEnough, loaded$ } from './loaded-heuristic';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../../logging/LoggerServiceFactory';

//types/ interfaces
function isRef(x: RefOrObservable): x is firebase.database.Reference {
	return (<any>x).ref !== undefined;
}
function isObservable(x: RefOrObservable): x is Observable<AngularFireAction<DatabaseSnapshot<any>>> {
	return (<any>x).subscribe !== undefined;
}

export class FancyFirebaseList<FancyModel, SomeOtherActionName extends FancyAction> {
	/** Provides syncronous access to the current state of the list. Use only for "point access", in general prefer a subscription. */
	public get syncList() {
		// tslint:disable-next-line: deprecation
		return this._internalList;
	}

	constructor(
		protected angularLand: AngularLandConnection,
		path: RefOrObservable,
		protected actionCtor: new (...args: any[]) => SomeOtherActionName,
		fancyJoins?: FancyIdJoin<FancyModel, any> | [FancyIdJoin<FancyModel, any>]
	) {
		this.actionTracker = this.angularLand.fancyActionTrackerFactory.create();
		this.logger = LoggerServiceFactory.create('FancyFirebaseList');

		if (fancyJoins) {
			if (Array.isArray(fancyJoins)) {
				this.fancyJoins = fancyJoins;
			} else {
				this.fancyJoins = [fancyJoins];
			}
			this.fancyJoins.forEach((fancyJoin) => {
				fancyJoin.start(this);
			});
		}

		const subject = new Subject<SomeOtherActionName>();
		this.rawSubject = subject;
		this.list$ = new BehaviorSubject(new Map());
		this.appEvent$ = new Subject();
		const mappedAppEvents = this.handleCustomEvents(this.appEvent$);

		this.supplementalUpdate$ = new Subject();
		const mappedSupplementalEvents = this.handleSupplementalEvents(this.supplementalUpdate$).pipe(
			takeUntil(this.shutdownSubject)
		);

		const baseFirebaseEvent$ = merge(
			this.setupInputObservable(path).pipe(
				takeUntil(this.shutdownSubject),
				map((action) => {
					// if(action.key === "-La0ul-iHheK9bTeljP3"){debugger;}
					if (this.enableLogging) {
						this.eventHistory.push(action.type + ' ' + action.key);
					}

					let child: FancyModel;
					let newAction: SomeOtherActionName;
					if (action.type === FancyActionTypes.childAdded) {
						if (this._internalList.has(action.key)) {
							return null;
						} //allows for custom adds, might be better to convert to child_changed
						child = this.$$added(action);
						newAction = this.constructAction(this.actionCtor, child, action.type, action.key);
						this._internalList.set(action.key, child);
					} else if (action.type === FancyActionTypes.childChanged) {
						child = this._internalList.get(action.key);
						newAction = this.constructAction(this.actionCtor, child, action.type, action.key);

						if (!child && fbReporting) {
							fbReporting.reportRealWarning(
								'child undefined in firebaseUpdate call',
								Object.assign(makeStack(new Error('child undefined in firebaseUpdate call')), {
									actionKey: action ? action.key : 'undefined',
									actionType: action ? action.type : 'undefined',
									actionPayload: action && action.payload ? action.payload.val() : 'undefined',
									eventHistory: this.eventHistory,
									ticketCount: this._internalList && this._internalList.size ? this._internalList.size : 0,
								})
							);
						}

						// this.decoratePrev(newAction, child);
						this.$$updated(child, action);
						// child.update(action.payload.val());
					} else if (action.type === FancyActionTypes.childRemoved) {
						if (!this._internalList.has(action.key)) {
							return null;
						} //allows for custom local removes
						child = this._internalList.get(action.key);
						newAction = this.constructAction(this.actionCtor, child, action.type, action.key);
						// this.decoratePrev(newAction, child);
						this.$$removed(child); //chance to react
						this._internalList.delete(action.key);
					} else if (action.type === 'child_moved') {
						this.$$moved(child); //chance to react
					}
					// return {"child": child, "action": action.type};
					// return new FancyAction(child, action.type, action.key);
					return newAction;
					// return new SomeOtherActionName(child, action.type, action.key);
				}),
				filter((action) => !!action)
			),
			mappedSupplementalEvents,
			mappedAppEvents as Observable<SomeOtherActionName>,
			this.joinEvent$
		);

		//just... just salvage this mess, preseve the behavior
		const joinedAndSuppressed$ = baseFirebaseEvent$.pipe(
			//this doesn't feel like the "best" way to do this
			tap((action) => {
				this.applyFancyJoinDataToBaseAction(action);
			}),
			filter((action) => {
				const stop = this.suppressActionsForLoading;
				if (this.suppressActionsForLoading) {
					this.suppressedActions.push(action);
					// logger.log('this.suppressedaction', this.suppressedActions)
				}
				return !stop;
			})
		);

		const runSuppressed$ = this.stopSuppressing$.pipe(
			mergeMap((no) => {
				const list = this.suppressedActions;
				this.suppressActionsForLoading = false;
				this.suppressedActions = [];
				return from(list);
			})
		);

		this._firebaseEvent$ = merge(joinedAndSuppressed$, runSuppressed$).pipe(
			catchError((err) => {
				fbReporting.reportError(err);
				this.logger.error('FancyFirebase error - ', err, this);
				throw err;
			}),
			observeOn(queueScheduler),
			tap(() => {
				this.hasRecievedEvents = true;
				this.list$.next(this._internalList);
			})
		);

		this.loadedList$ = combineLatest([this.list$, defer(() => this.loaded$())]).pipe(
			map((obj) => obj[0]),
			takeUntil(this.shutdownSubject)
		);

		//no, you're wrong rxjs, this is ConnectableObservable. Maybe you'll be right in a later version
		this.rawEvent$ = this._firebaseEvent$.pipe(
			multicast(subject)
		) as ConnectableObservable<SomeOtherActionName>;
		this.rawEvent$.connect();
	}
	private rawSubject: Subject<SomeOtherActionName>; //temp
	private _isDestroyed = false;

	/** list of functions to be called on destruction */
	private cleanupList: Array<() => void> = [];

	protected shutdownSubject = new Subject<void>();

	public hasRecievedEvents = false;

	//some code to log firebase events
	protected enableLogging = false;
	protected eventHistory = [];

	protected addActions: any = { [FancyActionTypes.childAdded]: FancyActionTypes.childAdded };
	protected removeActions: any = { [FancyActionTypes.childRemoved]: FancyActionTypes.childRemoved };

	/** if true none of the external observables will emit and events will be collected */
	public suppressActionsForLoading = false;
	protected stopSuppressing$ = new Subject();
	private suppressedActions = [];
	protected subscriptions: Subscription[] = [];

	public loaded = false;
	public listRef: AngularFireList<FancyModel>;

	protected _firebaseEvent$: Observable<any>; //oh screw you typescript

	/** @deprecated Direct access of _internalList is deprecated, prefer subscription or if necessary use syncList getter */
	public _internalList = new Map<string, FancyModel>();

	/** TODO - move private somehow */
	public appEvent$: Subject<SomeOtherActionName>;
	protected supplementalUpdate$: Subject<SomeOtherActionName>;

	public joinEvent$ = new Subject<FancyAction>();
	//public joinEvent$ = new Subject<FancyAction>().pipe(this.decorateActionWithPrev(action => this._internalList.get(action.key)));

	/** Subscribe to recieve raw "this ticket changed events" */
	public rawEvent$: ConnectableObservable<SomeOtherActionName>;
	/** Subscribe to recieve an updated list of tickets events. */
	public list$: BehaviorSubject<Map<string, FancyModel>>;
	public loadedList$: Observable<Map<string, FancyModel>>;

	private fancyJoins?: [FancyIdJoin<FancyModel, any>];

	public actionTracker: IFancyActionTrackerObj;
	protected logger: ILoggerService;

	/** generate a new firebase id suitable to be used on a new child */
	public generateNewChildKey() {
		return this.listRef.query.ref.push().key;
	}

	public constructAction<SomeOtherActionName>(
		ctor,
		payload: any,
		type: string,
		key?: string
	): SomeOtherActionName {
		return new ctor(payload, type, key);
	}

	/** called internally by the event chain. Can also be used to "essentially" recalc the join data.
	 * Only needed in _extremely_ fringe cases
	 */
	protected applyFancyJoinDataToBaseAction(action: FancyAction) {
		if (this.fancyJoins) {
			this.fancyJoins.forEach((fancyJoin) => {
				fancyJoin.manipBase(action);
			});
		}
	}

	private setupInputObservable(path: RefOrObservable) {
		// if (typeof path === 'string') {
		// 	this.listRef = this.angularLand.angularFireDatabase.list(path);
		// 	return this.listRef.stateChanges();
		// }
		if (isRef(path) && !(path as any).key) {
			//quack (it's a query I guess)
			//ridiculous workaround for the stupid syntax angularfire changed to
			this.listRef = this.angularLand.angularFireDatabase.list(<any>path.ref, () => path);
			return this.listRef.stateChanges();
		} else if (isRef(path)) {
			//it's actually valid to pass a ref, the typescript definition is dumb
			this.listRef = this.angularLand.angularFireDatabase.list(<any>path);
			return this.listRef.stateChanges();
		} else if (isObservable(path)) {
			return path;
		} else {
			throw new Error('the hell you pass in here');
		}
	}

	/**
	 * A thing that filters down events that happen in a render frame to at most one "non-significant" event per key.
	 * Or n significant events. Is useful because a lot of events will fire back to back for the same thing.
	 * And most of the tiome all that's wanted is a "data changed" notification
	 * @param  significantActions An object containing a key === value list of actionTypes that _need_ to happen.
	 * @param  sourceObservable   The observable to use as a source, defaults to rawEvent$.
	 * 	This would allow you to use safeRawEvent$ or something like that.
	 * @return                    Returns the transformed observable
	 */
	public getSoakedEvents$(significantActions?: Object, sourceObservable?: Observable<SomeOtherActionName>) {
		// if(!this.suppressActionsForLoading) {return}
		//potentially create a variance here to say that there are no significant actions so that the forEach can be skipped
		// also consider creating a shared one if the significant events are consistent (maybe in the extended class)
		if (!significantActions) {
			significantActions = {};
		}
		if (!sourceObservable) {
			sourceObservable = this.rawEvent$;
		}

		const baseTick$ = renderQueue.ob$.pipe(takeUntil(this.shutdownSubject));
		// let baseTick$ = renderQueue.fakeFrame$.pipe(takeUntil(this.shutdownSubject));
		const delay250$ = baseTick$.pipe(drop(15, false), takeUntil(this.shutdownSubject));

		// var lastTime;
		// delay250$.subscribe(() => {logger.log('delay250', Date.now() - lastTime, this); lastTime = Date.now();} )

		// console.time("delay");
		// baseTick$.subscribe(() => {console.timeEnd("delay"); console.time("delay")} )

		//ideally we shouldn't use a variable, but instead have a start/ stop observable
		// will _likely_ have to write an operator though
		let skip = false;

		//cull the animation frame updates when there's been no ticket updates
		this.rawEvent$.subscribe((thing) => {
			skip = false;
		});
		const settle$ = this.rawEvent$.pipe(debounce(() => delay250$));
		settle$.subscribe((thing) => {
			skip = true;
		});
		const tick$ = baseTick$.pipe(
			// tap(tick => {logger.log('tick$', tick)}),
			filter((t) => !skip), //ignore events while skipped
			// tap(tick => {logger.log('tick$', tick)}),
			//now multicast the suppressed one to avoid the splice when idle
			// share(),
			publish()
		);
		(tick$ as ConnectableObservable<number>).connect();
		// tick$.subscribe(test => logger.log('tick$', test));

		return this.rawEvent$.pipe(
			// tap(raw => logger.log('raw', raw)),
			groupBy((action) => action.key),
			mergeMap((action$) => {
				// logger.log('merge map', action$);
				return action$.pipe(
					// tap(tick => {logger.log('action$', tick, 'skip:', skip)}),
					// buffer(tick$),
					alternativeBuffer(tick$), //a special buff that doesn't emit empty arrays
					// tap(raw => logger.log('alt buff', raw)),
					// map(t => [t]),
					mergeMap((list) => {
						// debugger;
						// logger.log('list', list);
						// if(list.length === 0){return empty();}
						if (list.length === 1) {
							return of(list[0]);
						}

						const matched = [];
						list.forEach((e) => {
							if (significantActions[e.type] !== undefined) {
								matched.push(e);
							}
						});
						if (matched.length) {
							return from(matched);
						}
						return of(list[0]);
					})
				);
			})
			// takeUntil(this.shutdownSubject)
		);
	}

	/**
	 * Emits when a loaded heuristic passes. Avoid unless absolutely necessary
	 * @param cb Optional callback, if provided it will be called with the number of elements on the firebase snapshot.
	 * This can be used to allow the check if it's "off by one" for example. Leaving it out allows for everything to be "good enough"
	 */
	public loaded$(cb?: (val: number) => boolean) {
		if (!this.listRef.query.ref) {
			throw new Error('cannot check loaded on a list without a defined ref');
		}
		return loaded$(this.listRef.query.ref, this.list$.asObservable()).pipe(goodEnough(cb));
	}

	/**
	 * Re-emits an action for every child in the list, allowing a potential observer to start fully
	 * in sync despite not subscribing since the beginning. Typically the observer
	 * would be expected to subscribe to this (executes syncronously) and then subscribe to
	 * the rawEvent$ proper.
	 */
	public replayRawEvent$(): Observable<SomeOtherActionName> {
		return from(this._internalList as any).pipe(
			map((thing: [string, FancyModel]) => {
				return this.constructAction<SomeOtherActionName>(
					this.actionCtor,
					thing[1],
					FancyActionTypes.childReplayed,
					thing[0]
				);
			})
		);
	}

	/**
	 * A special variant of rawEvent$ that first replays the existing data as events,
	 * then runs off of the standard rawEvent$. Should be careful with this, it's
	 * pretty likely that it will run with a different execution order than standard
	 * rawEvent$. This _may_ be a problem in some cases.
	 */
	public safeRawEvent$() {
		return concat(this.replayRawEvent$(), this.rawEvent$);
	}

	/** get a custom observer for a list item that inits to the current known value, than emits the rawEvents for only that item */
	public getChildOb$(id: string) {
		const first = this._internalList.get(id);
		const firstAction = this.constructAction<SomeOtherActionName>(
			this.actionCtor,
			first,
			FancyActionTypes.childReplayed,
			id
		);
		return concat(of(firstAction), this.rawEvent$.pipe(filter((action) => action.key === id)));
	}

	/**
	 * Called by the list building code,
	 * @param  action the angularfire snapshot
	 * @return        The constructed child object
	 */
	public $$added(action: LocalAction<LocalSnapshot<any>>) {
		const obj = action.payload.val();
		//TODO - create fancyfirebaseprimative class with default handling
		if (typeof obj !== 'object') {
			this.logger.log(
				'base FancyFirebase list does not support primative values. Extend and override with object mapping'
			);
		} else if (obj) {
			obj.$id = action.key;
		}
		return obj;
	}

	public $$updated(child: FancyModel, action: AngularFireAction<DatabaseSnapshot<any>>) {
		//TODO - create fancyfirebaseprimative class with default handling
		const payload = action.payload.val();
		if (typeof payload !== 'object') {
			this.logger.log(
				'base FancyFirebase list does not support primative values. Extend and override with object mapping'
			);
		}
		Object.assign(child, payload);
	}

	public $$removed(child: FancyModel) {}

	public $$moved(child: FancyModel) {}

	//pass in a supplemental obserable to subscribe to, optionally wrapping it in an action
	public addSupplementalObservable(
		obs: Observable<SomeOtherActionName>,
		actionType?: FancyActionTypes,
		key?
	) {
		if (!actionType) {
			this.subscriptions.push(obs.subscribe(this.supplementalUpdate$));
		} else {
			this.subscriptions.push(
				obs
					.pipe(map((thing) => this.constructAction(this.actionCtor, thing, actionType, key || null)))
					.subscribe(this.supplementalUpdate$)
			);
		}
	}

	//override with the custom observable chain
	public handleCustomEvents(baseObservable) {
		return baseObservable.pipe(
			switchMap((action: FancyAction) => {
				//some debug shit
				//remove item
				if (this._internalList.has(action.key) && action.payload === null) {
					const item: any = this._internalList.get(action.key);
					this._internalList.delete(action.key);
					return of(new FancyAction(item, action.type));
				}
				//update item
				else if (this._internalList.has(action.key)) {
					const item: any = Object.assign(this._internalList.get(action.key), action.payload);
					return of(new FancyAction(item, action.type));
				}
				//add item
				else if (action.key) {
					this._internalList.set(action.key, action.payload);
				}
				return of(new FancyAction(action.payload, action.type));
			})
		);
		// return baseObservable;
	}

	public handleSupplementalEvents(baseObservable): Observable<any> {
		return EMPTY;
	}

	public isAddAction(action: IHasType | string) {
		return isActionIncluded(action, this.addActions);
	}
	public isRemoveAction(action: IHasType | string) {
		return isActionIncluded(action, this.removeActions);
	}
	public isChangeAction(action: IHasType | string) {
		return !isActionIncluded(action, this.addActions, this.removeActions);
	}

	/**
	 * Push any functions that want to invoke destruction calls when this class is destroyed.
	 * @param fns List of functions that want to be called
	 */
	public addDestroyer(...fns: Array<() => void>) {
		this.cleanupList.push(...fns);
	}

	public destroy() {
		if (this._isDestroyed) {
			this.logger.log('WARN - tried to destroy a destroyed firebaseList', this);
			return;
		}
		this.shutdownSubject.next();
		this.rawSubject.complete();
		this.appEvent$.complete();
		this.joinEvent$.complete();
		this.list$.complete();
		this.list$ = null;
		this.actionTracker.actionTracker$.destroy(this.actionTracker.unsubscriber);
		this.actionTracker = null;
		this.stopSuppressing$.complete();
		this.supplementalUpdate$.complete();
		this.shutdownSubject.complete();

		this.subscriptions.forEach((sub) => sub.unsubscribe());

		this.cleanupList.forEach((fn) => fn());
		this.cleanupList = [];

		//probably not necessary
		// this.rawEvent$.co
		this._firebaseEvent$ = null;
		this.rawSubject = null;
		this.rawEvent$ = null;
		this.loadedList$ = null;

		this._internalList = null;

		if (this.fancyJoins) {
			this.fancyJoins.forEach((f) => f.destroy());
		}
		this._isDestroyed = true;
	}

	/**
	 * Called by the join class, if you use it, you'll be expected to override this
	 * method. It's expected to take a child of this class (extended), and an action of another
	 * extended instance of this class and apply the otherAction to this child.
	 * @param  baseKey     The key on the child to map.
	 * @param  child       [description]
	 * @param  otherAction [description]
	 * @return             void
	 */
	/*
	applyJoinData(baseKey: string, child:FancyModel, otherChild:any){
		logger.log('YOU MUST CONSTRUCT MORE OVERRIDE');
		// switch(baseKey){
		// 	child.bleh = otherAction;
		// }
		// return child;
	}
	getDataForKey(baseKey: string, child:FancyModel){
		logger.log('YOU MUST CONSTRUCT MORE OVERRIDE');
		return child[baseKey];
	}
	getPrevDataForKey(baseKey: string, child:FancyModel){
		return "you screwed up";
	}
	*/
}
